import { Component, ElementRef, Inject, QueryList, ViewChildren } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { NgForOf, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';

import { PatientService } from '@core/http/patient.service';
import { HttpMesageHandlerService } from '@core/service/http-mesage-handler';
import { HttpErrorResponse } from '@angular/common/http';
import { PIN_DIALOG_COPY, PinDialogMode } from '@core/constants/pin-dialog.copy';

export type PatientPinDialogData = {
  mode: PinDialogMode;
  title?: string;
  message?: string;
  optional?: boolean;
};


type PinValidation =
  | { ok: true; pin: string }
  | { ok: false; pin: string; reason: 'FORMAT' | 'REPEATED' | 'SEQUENCE' | 'INCOMPLETE' };

type RemoteStatus = 'idle' | 'checking' | 'valid' | 'invalid';

@Component({
  selector: 'app-patient-pin-dialog',
  standalone: true,
  imports: [
    NgIf,
    NgForOf,
    NgSwitch,
    NgSwitchCase,
    NgSwitchDefault,
    MatDialogModule,
    MatButtonModule,
    MatTooltipModule
  ],
  templateUrl: './patient-pin-dialog.component.html',
  styleUrl: './patient-pin-dialog.component.scss',
})
export class PatientPinDialogComponent {
  mode!: PinDialogMode;
  // ===== OTP (6 cuadritos) =====
  pinDigits = Array.from({ length: 6 });
  pinOtp: string[] = ['', '', '', '', '', ''];

  @ViewChildren('pinDigit') pinDigitInputs!: QueryList<ElementRef<HTMLInputElement>>;

  // UI
  errorMsg = '';

  // ✅ Estado remoto (backend)
  pinRemoteStatus: RemoteStatus = 'idle';

  // ✅ Confirmar sólo si el pin está validado en backend
  canConfirm = false;

  // Para evitar validaciones repetidas del mismo pin
  private lastValidatedPin = '';

  constructor(
    private dialogRef: MatDialogRef<PatientPinDialogComponent, string | null>,
    @Inject(MAT_DIALOG_DATA) public data: PatientPinDialogData,
    private patientService: PatientService,
    private httpMsgHandler: HttpMesageHandlerService
  ) {
    const cfg = PIN_DIALOG_COPY[this.data.mode];

    this.data = {
      mode: this.data.mode,
      title: this.data?.title ?? cfg.title,
      message: this.data?.message ?? cfg.message,
      optional: this.data?.optional ?? cfg.optional,
    };

    this.mode = data.mode;

  }

  ngAfterViewInit(): void {
    this.resetOtp();
  }

  // =========================
  // OTP helpers
  // =========================
  private focusOtpIndex(i: number) {
    const list = this.pinDigitInputs?.toArray?.() ?? [];
    const el = list[i]?.nativeElement;
    if (el) {
      el.focus();
      el.select();
    }
  }

  private resetOtp() {
    this.pinOtp = ['', '', '', '', '', ''];
    this.errorMsg = '';
    this.canConfirm = false;
    this.pinRemoteStatus = 'idle';
    this.lastValidatedPin = '';
    setTimeout(() => this.focusOtpIndex(0), 0);
  }

  /**
   * (Opcional) Validación local: formato básico (puedes dejarla o quitarla)
   * Yo la dejo para no pegarle al API si ni siquiera son 6 dígitos.
   */
  private validatePinLocal(pin: string): PinValidation {
    const raw = (pin ?? '').toString().trim();

    if (raw.length < 6) return { ok: false, pin: raw, reason: 'INCOMPLETE' };
    if (!/^\d{6}$/.test(raw)) return { ok: false, pin: raw, reason: 'FORMAT' };

    // si quieres SOLO backend, comenta esto:
    const distinct = new Set(raw.split(''));
    if (distinct.size !== raw.length) return { ok: false, pin: raw, reason: 'REPEATED' };

    for (let i = 0; i < raw.length - 2; i++) {
      const a = raw.charCodeAt(i) - 48;
      const b = raw.charCodeAt(i + 1) - 48;
      const c = raw.charCodeAt(i + 2) - 48;

      const asc = b === a + 1 && c === b + 1;   // 123
      const desc = b === a - 1 && c === b - 1;  // 321
      if (asc || desc) return { ok: false, pin: raw, reason: 'SEQUENCE' };
    }

    return { ok: true, pin: raw };
  }

  private setErrorFromLocal(v: PinValidation, show = false) {
    if (!show) {
      this.errorMsg = '';
      return;
    }

    if (v.ok) {
      this.errorMsg = '';
      return;
    }

    switch (v.reason) {
      case 'INCOMPLETE':
        this.errorMsg = 'INGRESA 6 DÍGITOS.';
        break;
      case 'FORMAT':
        this.errorMsg = 'EL PIN DEBE SER DE 6 DÍGITOS NUMÉRICOS.';
        break;
      case 'REPEATED':
        this.errorMsg = 'EL PIN NO PUEDE REPETIR NÚMEROS.';
        break;
      case 'SEQUENCE':
        this.errorMsg = 'EVITA SECUENCIAS COMO 123 O 321.';
        break;
    }
  }

  /**
   * ✅ Valida contra backend:
   * - Si está ok => habilita confirmar + muestra ✅
   * - Si no => deshabilita confirmar + muestra ❌
   */
  private validatePinRemote(pin: string) {

    // 🔒 SOLO PACIENTE valida contra este endpoint
    if (this.mode !== 'PACIENTE') {
      this.pinRemoteStatus = 'valid';
      this.canConfirm = true;
      this.errorMsg = '';
      return;
    }

    // ⬇️ A partir de aquí SOLO PACIENTE
    if (
      pin === this.lastValidatedPin &&
      (this.pinRemoteStatus === 'valid' || this.pinRemoteStatus === 'invalid')
    ) {
      return;
    }

    this.pinRemoteStatus = 'checking';
    this.canConfirm = false;

    this.patientService.validatePinPaciente({ pin }).subscribe({
      next: (res: any) => {
        const ok =
          res === true ||
          res?.data === true ||
          res?.success === true ||
          res?.isValid === true ||
          res?.data?.isValid === true;

        this.lastValidatedPin = pin;

        if (!ok) {
          this.pinRemoteStatus = 'invalid';
          this.canConfirm = false;
          this.errorMsg = 'EL PIN ACTUAL NO COINCIDE. VERIFÍCALO.';
          return;
        }

        this.pinRemoteStatus = 'valid';
        this.canConfirm = true;
        this.errorMsg = '';
      },
      error: (err: HttpErrorResponse) => {
        this.pinRemoteStatus = 'invalid';
        this.canConfirm = false;
        this.httpMsgHandler.handleMsg(err);
      }
    });
  }

  private recomputeState(showLocalError: boolean) {
    const pin = this.pinOtp.join('');
    const local = this.validatePinLocal(pin);

    // Si NO pasó local => no llamamos backend
    if (!local.ok) {
      this.setErrorFromLocal(local, showLocalError);

      this.pinRemoteStatus = pin.length === 0 ? 'idle' : 'idle'; // se queda idle hasta tener 6
      this.canConfirm = true;
      this.lastValidatedPin = '';
      return { pin, localOk: true };
    }

    // local ok => limpia error local y si ya son 6, pega al API
    this.setErrorFromLocal(local, false);

    return { pin, localOk: true };
  }

  // =========================
  // Eventos OTP
  // =========================
  onOtpInput(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    let v = (input.value ?? '').replace(/\D/g, '');
    if (v.length > 1) v = v.slice(-1);

    const arr = [...this.pinOtp];
    arr[index] = v;
    this.pinOtp = arr;

    const filledAll = !this.pinOtp.includes('');
    this.recomputeState(filledAll);

    if (v && index < 5) this.focusOtpIndex(index + 1);
  }

  onOtpKeyDown(event: KeyboardEvent, index: number) {
    const key = event.key;

    const allowed =
      /^[0-9]$/.test(key) ||
      key === 'Backspace' ||
      key === 'Delete' ||
      key === 'ArrowLeft' ||
      key === 'ArrowRight' ||
      key === 'Tab';

    if (!allowed) {
      event.preventDefault();
      return;
    }

    if (key === 'ArrowLeft') {
      event.preventDefault();
      this.focusOtpIndex(Math.max(0, index - 1));
      return;
    }

    if (key === 'ArrowRight') {
      event.preventDefault();
      this.focusOtpIndex(Math.min(5, index + 1));
      return;
    }

    if (key === 'Backspace') {
      const arr = [...this.pinOtp];
      const current = arr[index];

      if (!current && index > 0) {
        event.preventDefault();
        arr[index - 1] = '';
        this.pinOtp = arr;

        // al borrar, reseteamos estado remoto
        this.pinRemoteStatus = 'idle';
        this.canConfirm = false;
        this.lastValidatedPin = '';

        this.recomputeState(false);
        this.focusOtpIndex(index - 1);
      } else {
        setTimeout(() => {
          const list = this.pinDigitInputs?.toArray?.() ?? [];
          const el = list[index]?.nativeElement;
          const raw = (el?.value ?? '').replace(/\D/g, '');
          arr[index] = raw ? raw.slice(-1) : '';
          this.pinOtp = arr;

          this.pinRemoteStatus = 'idle';
          this.canConfirm = false;
          this.lastValidatedPin = '';

          this.recomputeState(false);
        }, 0);
      }
      return;
    }

    if (/^[0-9]$/.test(key)) {
      event.preventDefault();
      const arr = [...this.pinOtp];
      arr[index] = key;
      this.pinOtp = arr;

      const filledAll = !this.pinOtp.includes('');
      this.recomputeState(filledAll);

      if (index < 5) this.focusOtpIndex(index + 1);
    }
  }

  onOtpPaste(event: ClipboardEvent) {
    event.preventDefault();
    const text = (event.clipboardData?.getData('text') ?? '').replace(/\D/g, '').slice(0, 6);
    if (!text) return;

    const arr = ['', '', '', '', '', ''];
    for (let i = 0; i < 6; i++) arr[i] = text[i] ?? '';
    this.pinOtp = arr;

    // pegado: valida local + remoto
    this.recomputeState(true);

    const nextEmpty = arr.findIndex(x => !x);
    setTimeout(() => this.focusOtpIndex(nextEmpty === -1 ? 5 : nextEmpty), 0);
  }

  // =========================
  // Acciones
  // =========================
  continueWithoutPin(): void {
    this.dialogRef.close(null);
  }

  confirm(): void {
    const pin = this.pinOtp.join('');

    // fuerza validación local (por si le dio confirm sin completar bien)
    const local = this.validatePinLocal(pin);
    if (!local.ok) {
      this.setErrorFromLocal(local, true);
      const firstEmpty = this.pinOtp.findIndex(x => !x);
      this.focusOtpIndex(firstEmpty === -1 ? 5 : firstEmpty);
      return;
    }

    this.dialogRef.close(pin);
  }

  clear(): void {
    this.resetOtp();
  }
}
